import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import {
  Star,
  AreaChart,
  ArrowRight,
  Heart,
  Mail,
  Users,
  Briefcase,
  BarChart3,
  Database,
  Clock,
  CalendarDays
} from "lucide-react";

// Feature categories with new consolidated pages
const featureCategories = [
  {
    title: "Customer Engagement",
    description: "Build relationships and grow your customer base",
    features: [
      {
        name: "Reviews & Feedback",
        description: "Collect authentic customer reviews with QR codes, mobile forms, and Google Business Profile integration.",
        icon: Star,
        link: "/features/reviews",
      },
      {
        name: "Loyalty & Rewards",
        description: "Create digital loyalty cards and coupons for Apple Wallet and Google Pay to drive repeat business.",
        icon: Heart,
        link: "/features/loyalty",
      },
      {
        name: "Marketing & Communications",
        description: "Email campaigns, Canva designs, automation, subscriptions, and team messaging all in one place.",
        icon: Mail,
        link: "/features/marketing",
      },
    ]
  },
  {
    title: "Workforce Management",
    description: "Streamline operations and empower your team",
    features: [
      {
        name: "Employee & Workforce",
        description: "QR time tracking, shift scheduling, payroll for 25+ countries, and employee portal in one solution.",
        icon: Users,
        link: "/features/workforce",
      },
      {
        name: "Recruitment & Hiring",
        description: "Post jobs, manage applications, schedule interviews, and onboard new hires seamlessly.",
        icon: Briefcase,
        link: "/features/recruitment",
      },
    ]
  },
  {
    title: "Business Intelligence",
    description: "Make smarter decisions with data and insights",
    features: [
      {
        name: "Analytics & Insights",
        description: "Real-time dashboards, funnel tracking, Sankey diagrams, and comprehensive business intelligence.",
        icon: BarChart3,
        link: "/features/analytics",
      },
      {
        name: "CRM & Customer Data",
        description: "Organize contacts, create segments with smart tags, and track complete customer history.",
        icon: Database,
        link: "/features/crm",
      },
    ]
  }
];

// Business types that benefit from our solution
const businessTypes = [
  "Restaurants & Cafés",
  "Retail Stores",
  "Hair & Beauty Salons",
  "Fitness Studios",
  "Home Services",
  "Healthcare Practices",
  "Automotive Services",
  "Professional Services"
];

const FeaturesPage = () => {
  return (
    <div className="min-h-screen w-full flex flex-col bg-background">
      <Navbar />

      <main className="flex-grow">
        {/* Hero Section */}
        <section className="py-24 sm:py-32 relative overflow-hidden">
          {/* Subtle background gradient */}
          <div className="absolute inset-0 -z-10">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-grow/5 to-secondary/5" />
          </div>

          {/* Floating benefit badges */}
          <div className="absolute bottom-64 left-10 hidden xl:block">
            <div className="bg-card/80 backdrop-blur-sm border border-border rounded-xl p-4 shadow-lg">
              <div className="flex items-center gap-2">
                <Star className="h-5 w-5 text-yellow-500" fill="currentColor" />
                <div>
                  <div className="text-sm font-semibold">Increase Online Ratings</div>
                  <div className="text-xs text-muted-foreground">Build your reputation</div>
                </div>
              </div>
            </div>
          </div>

          <div className="absolute bottom-52 right-10 hidden xl:block">
            <div className="bg-card/80 backdrop-blur-sm border border-border rounded-xl p-4 shadow-lg">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-grow" />
                <div>
                  <div className="text-sm font-semibold">Save Time</div>
                  <div className="text-xs text-muted-foreground">Automate your workflow</div>
                </div>
              </div>
            </div>
          </div>

          <div className="absolute bottom-[5%] left-32 hidden xl:block">
            <div className="bg-card/80 backdrop-blur-sm border border-border rounded-xl p-4 shadow-lg">
              <div className="flex items-center gap-2">
                <CalendarDays className="h-5 w-5 text-primary" />
                <div>
                  <div className="text-sm font-semibold">Organize Everything</div>
                  <div className="text-xs text-muted-foreground">All-in-one platform</div>
                </div>
              </div>
            </div>
          </div>

          <div className="absolute bottom-[5%] right-32 hidden xl:block">
            <div className="bg-card/80 backdrop-blur-sm border border-border rounded-xl p-4 shadow-lg">
              <div className="flex items-center gap-2">
                <Heart className="h-5 w-5 text-secondary" />
                <div>
                  <div className="text-sm font-semibold">Attract & Retain Customers</div>
                  <div className="text-xs text-muted-foreground">Build loyalty</div>
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h1 className="text-4xl font-bold tracking-tight text-foreground sm:text-5xl mb-6">
                Powerful Features to Grow Your Business
              </h1>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Collect authentic reviews, build loyalty, and streamline operations with our all-in-one platform designed specifically for local businesses.
              </p>
              <div className="mt-12 flex flex-col sm:flex-row justify-center gap-4">
                <Button size="lg" className="px-8 bg-primary hover:bg-primary/90 text-lg" asChild>
                  <Link to="/sign-up">
                    Get Started Free <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="px-8 text-lg"
                  onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Contact Us
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Section - Minimal Horizontal Slider */}
        <section className="py-16 sm:py-20">
          <style>{`
            @keyframes fadeInUp {
              from {
                opacity: 0;
                transform: translateY(20px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
            .animate-stagger-1 {
              animation: fadeInUp 0.6s ease-out 0.1s both;
            }
            .animate-stagger-2 {
              animation: fadeInUp 0.6s ease-out 0.3s both;
            }
            .animate-stagger-3 {
              animation: fadeInUp 0.6s ease-out 0.5s both;
            }
          `}</style>

          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl mb-3">
                Why Local Businesses Choose Tag-Inn
              </h2>
              <p className="text-base text-muted-foreground max-w-2xl mx-auto">
                In today's digital landscape, local businesses face unique challenges that our platform is specifically designed to solve.
              </p>
            </div>

            <div className="overflow-x-auto snap-x snap-mandatory -mx-6 px-6">
              <div className="flex gap-4 w-max md:w-auto md:grid md:grid-cols-3 md:gap-6">
                <div className="animate-stagger-1 min-w-[280px] md:min-w-0 snap-center bg-card rounded-lg p-4 border border-border transition-all duration-300 hover:scale-[1.02] hover:shadow-lg hover:border-primary/50">
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center mb-3">
                    <Star className="h-5 w-5 text-primary transition-colors" />
                  </div>
                  <h3 className="text-base font-semibold text-foreground mb-2">The Review Dilemma</h3>
                  <p className="text-sm text-muted-foreground">
                    Happy customers rarely leave reviews without prompting. Our platform helps you systematically collect feedback from satisfied customers.
                  </p>
                </div>

                <div className="animate-stagger-2 min-w-[280px] md:min-w-0 snap-center bg-card rounded-lg p-4 border border-border transition-all duration-300 hover:scale-[1.02] hover:shadow-lg hover:border-grow/50">
                  <div className="w-8 h-8 rounded-lg bg-grow/10 flex items-center justify-center mb-3">
                    <Users className="h-5 w-5 text-grow transition-colors" />
                  </div>
                  <h3 className="text-base font-semibold text-foreground mb-2">Employee Management</h3>
                  <p className="text-sm text-muted-foreground">
                    Juggling schedules, payroll, and time tracking across multiple tools is exhausting. We centralize everything in one dashboard.
                  </p>
                </div>

                <div className="animate-stagger-3 min-w-[280px] md:min-w-0 snap-center bg-card rounded-lg p-4 border border-border transition-all duration-300 hover:scale-[1.02] hover:shadow-lg hover:border-secondary/50">
                  <div className="w-8 h-8 rounded-lg bg-secondary/10 flex items-center justify-center mb-3">
                    <Heart className="h-5 w-5 text-secondary transition-colors" />
                  </div>
                  <h3 className="text-base font-semibold text-foreground mb-2">Customer Retention</h3>
                  <p className="text-sm text-muted-foreground">
                    Acquiring new customers costs 5x more than retaining existing ones. Our loyalty system builds lasting relationships.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid - Categorized */}
        <section className="py-24 sm:py-32">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl mb-6">
                Everything You Need in One Place
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Our comprehensive toolkit gives you everything you need to manage, grow, and scale your business.
              </p>
            </div>

            <div className="space-y-20">
              {featureCategories.map((category, categoryIndex) => (
                <div key={category.title}>
                  {/* Category Header */}
                  <div className="text-center mb-12">
                    <h3 className="text-2xl font-bold text-foreground mb-3">
                      {category.title}
                    </h3>
                    <p className="text-lg text-muted-foreground">
                      {category.description}
                    </p>
                  </div>

                  {/* Category Features */}
                  <div className={`grid grid-cols-1 ${category.features.length === 2 ? 'md:grid-cols-2 max-w-4xl mx-auto' : 'md:grid-cols-2 lg:grid-cols-3'} gap-8`}>
                    {category.features.map((feature) => (
                      <Link
                        key={feature.name}
                        to={feature.link}
                        className="group"
                      >
                        <div className="bg-card text-card-foreground rounded-xl p-8 border-2 border-border hover:border-primary transition-all duration-300 shadow-md hover:shadow-xl h-full">
                          <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
                            <feature.icon className="h-6 w-6 text-primary" />
                          </div>
                          <h4 className="text-xl font-semibold text-card-foreground mb-3 group-hover:text-primary transition-colors">
                            {feature.name}
                          </h4>
                          <p className="text-card-foreground/70 mb-4">
                            {feature.description}
                          </p>
                          <div className="flex items-center text-primary text-sm font-medium">
                            Learn more <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>

                  {/* Visual separator between categories (except last one) */}
                  {categoryIndex < featureCategories.length - 1 && (
                    <div className="mt-16 flex justify-center">
                      <div className="w-24 h-1 bg-gradient-to-r from-transparent via-border to-transparent" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Business Types */}
        <section className="py-24 sm:py-32 bg-gradient-to-br from-muted/30 via-background to-muted/20">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl mb-6">
                Perfect For All Local Businesses
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Whether you run a café, salon, or professional service, our platform adapts to your specific needs.
              </p>
            </div>

            <div className="flex flex-wrap justify-center gap-4">
              {businessTypes.map((business, index) => (
                <div
                  key={index}
                  className="bg-card border border-border rounded-full px-6 py-3 text-foreground font-medium hover:border-primary/50 transition-colors"
                >
                  {business}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 sm:py-32">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="relative isolate overflow-hidden bg-gradient-to-r from-primary/10 via-grow/10 to-secondary/10 backdrop-blur-sm px-6 py-24 text-center shadow-2xl sm:rounded-3xl sm:px-16 border border-muted">
              <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl mb-6">
                Ready to Get Started?
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-10">
                Start collecting reviews and building customer loyalty with Tag-Inn's free review features.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Button size="lg" className="px-8 bg-primary hover:bg-primary/90 text-lg" asChild>
                  <Link to="/sign-up">
                    Get Started Free <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="px-8 text-lg"
                  onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Contact Us
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default FeaturesPage;
